import type { BatchProgressView, Listed } from '@ctcells/api'
import type { Batch, BatchTracking, Recipe } from '@ctcells/contract'
import { MODULE_KEYS, isModuleKey, type ModuleKey } from '@ctcells/core'
import {
  batchStatusLabel,
  executionEventLabel,
  moduleLabel,
  moduleTypeKey,
  moduleTypeLabel,
  t,
} from '@ctcells/i18n'

import { batchDisplayName } from '../screens/batches/batchDisplayName.ts'
import { fullDateTimeText } from '../screens/format.ts'
import {
  sequenceDictionaryOfModuleType,
  sequenceText,
  speaksSequence,
} from '../screens/monitoring/latestStepModel.ts'
import { MANUAL_LOAD_WAIT_STATES } from '../screens/monitoring/moduleStateSets.ts'

import type {
  BatchStripBatch,
  BatchStripSegment,
  ControlLockView,
  StripBatchChoice,
} from './batchStripTypes.ts'
import { EMPTY_STEP_MEMORY, type StepMemory } from './stripStepMemory.ts'

/**
 * 작업 줄이 서버 응답을 화면 값으로 옮기는 **순수 함수 모음**. 훅도 JSX 도 없어서 단독으로 시험한다.
 *
 * 🔴 **버튼 활성에 쓰이는 값은 한 글자도 만들지 않는다.** `allowed_commands` 는 컨테이너가
 *    그대로 흘려보내고 여기는 «표시»만 만든다.
 */

/** 제어락 상태 — `@ctcells/api` `useControlLock()` 이 주는 모양 중 이 파일이 쓰는 만큼 */
export interface ControlLockLike {
  status: 'unknown' | 'none' | 'mine' | 'expired' | 'other'
  displayName?: string | undefined
  lease: {
    holder: string | undefined
    expiresAtMs: number
    receivedAtMs?: number | undefined
  } | null
}

/**
 * 작업 줄에 띄울 한 건을 고른다.
 *
 * 🔴 **정렬을 다시 하지 않는다.** 서버가 `created_at desc` 로 줄을 세워 주고
 *    (`app/services/batches.py` `_order()` — 「줄 세우기는 여기서 끝난다」),
 *    여기서 다시 세우면 쪽 밖의 행이 영영 안 보인다.
 * 🔴 고르는 기준은 **서버가 준 사실 하나**(`started_at` 이 있는가)다. 상태 어휘로 추론하지
 *    않는다 — 새 상태가 생겨도 이 규칙은 그대로 맞는다.
 *    ⚠ 「만들어만 둔 작업」(`started_at === null`)이 방금 만들어졌다는 이유로 **도는 작업을
 *      밀어내면 안 된다.** 상시 작업 줄은 진행 중인 것을 보는 자리다.
 * ⚠ 시작한 작업이 둘 이상이면 서버 차례상 «최근에 만든 것»이 뜬다.
 *
 * ## 사람이 고른 작업(`chosenBatchId` · eric 2026-10-22 「대기 중이거나 준비 중인 작업 선택해서.. 선택하게 해줘」)
 *
 * 차례는 **① 시작한 작업 ② 고른 작업 ③ 서버 차례 첫 건**이다.
 * 🔴 **시작한 작업이 언제나 이긴다.** 고른 값은 «시작 전 작업들 사이»에서만 뜻이 있다 — 도는 작업을
 *    사람의 선택으로 밀어내면 상시 작업 줄이 「지금 도는 것」을 놓친다.
 * 🔴 고른 작업이 **목록에 없거나(사라짐 · 종착은 서버가 뺀다) 끝났으면 조용히 무시한다.** 저장된 id 를
 *    믿고 붙들지 않는다 — 판정은 매번 서버 목록에 대 본다.
 * 🔴 **③(첫 건 대체)은 끌 수 있다**(`fallbackFirst: false`) — 통합 콘솔은 «사람이 고르기 전에는 시작 전 작업을 띠에 올리지
 *    않는다»(eric 2026-10-22 「이걸 선택하기 전에는 대기 이런 게 당연히 나오면 안 돼」). 그 차례는 ① → ② → 없음이다.
 *    표면 정책은 `stripPick.ts`(`useStripBatch`)가 한 곳에서 정한다.
 * ⚠ 인자 하나로 부르는 자리(`DevQuickBatch`)는 종전 규칙(① → ③) 그대로다 — 기본값을 바꾸지 마라.
 */
export function pickStripBatch(
  items: readonly Batch[],
  chosenBatchId?: string,
  options: { fallbackFirst?: boolean } = {},
): Batch | undefined {
  const started = items.find((batch) => batch.started_at !== null)
  if (started !== undefined) return started
  const chosen =
    chosenBatchId === undefined
      ? undefined
      : items.find((batch) => batch.batch_id === chosenBatchId && !isTerminalBatch(batch))
  if (chosen !== undefined) return chosen
  return options.fallbackFirst === false ? undefined : items[0]
}

/**
 * **고를 수 있는 작업인가** — 서버 어휘 그대로 «아직 시작하지 않은» 두 상태다.
 *
 * 🔴 서버 `app/services/batches.py` `NOT_STARTED_STATUSES = ("created", "queued")`(278행)를 옮겨 적었다 —
 *    시작 명령(`FROM_START`)이 받는 상태도 같은 둘이다. 도는 · 멈춘 · 끝난 작업은 고르지 않는다.
 * 🔴 `started_at` 도 같이 본다 — 상태 어휘와 서버 사실이 갈리면(시작 시각이 이미 있다) 고르지 않는 쪽이 안전하다.
 */
export const PICKABLE_BATCH_STATUSES: readonly Batch['status'][] = ['created', 'queued']

export function isPickableBatch(batch: Pick<Batch, 'status' | 'started_at'>): boolean {
  return PICKABLE_BATCH_STATUSES.includes(batch.status) && batch.started_at === null
}

/**
 * [작업 선택] 드롭다운의 재료(eric 2026-10-22 · `StripBatchPicker`) — **작업 줄이 이미 받은 활성 목록**에서 거른다(새 조회 없음).
 *
 * - 줄 = 고를 수 있는 작업(`isPickableBatch`)만, 서버 차례 그대로. 주 글자는 `Batch.name`(없으면 ID 전체), 보조는 ID 앞 8자리 · 상태.
 * - 체크 = 지금 줄에 떠 있는 작업(`shown`)이 시작 전일 때만.
 * - 🔴 **시작한 작업이 떠 있으면 비활성 + 사유**다(`pickStripBatch` 가 그 작업을 늘 먼저 띄우므로 골라도 바뀌지 않는다).
 */
export function stripBatchChoice(
  items: readonly Batch[],
  shown: Batch | undefined,
  onChoose: (batchId: string) => void,
  onAddBatch?: () => void,
): StripBatchChoice {
  const options = items.filter(isPickableBatch).map((batch) => ({
    id: batch.batch_id,
    name: batch.name ?? batch.batch_id,
    shortId: batch.batch_id.slice(0, 8),
    sub: batchStatusLabel(batch.status),
  }))
  const started = shown !== undefined && shown.started_at !== null
  return {
    options,
    currentId: shown !== undefined && isPickableBatch(shown) ? shown.batch_id : undefined,
    onChoose,
    ...(onAddBatch === undefined ? {} : { onAddBatch }),
    ...(started ? { disabled: true, disabledReason: t('reason.stripBatchStarted') } : {}),
  }
}

/**
 * **끝난 작업** — 더 돌지 않는 상태 둘.
 *
 * 🔴 **서버 어휘를 옮겨 적은 것이다**(`app/services/batches.py` `TERMINAL_STATUSES` =
 *    `("completed", "aborted")`). 같은 표가 `GET /batches?phase=active` 의 뺄 대상이기도 하다.
 *    둘이 갈리지 않는지는 `scripts/batch-status/serverDrift.test.ts` 가 실제 파이썬 원문을 읽어
 *    지킨다 — **여기 값만 손으로 늘리지 마라.**
 * 🔴 **나머지 다섯은 «아직 안 끝났다».** 특히 `paused`·`error` 는 사람 판정을 기다리는 상태라
 *    (`Batch.review_reason`) 「지난 것」으로 접으면 복구·완료확정 창구가 눈에서 멀어진다.
 */
export const TERMINAL_BATCH_STATUSES: readonly Batch['status'][] = ['completed', 'aborted']

/**
 * 그 작업이 끝났는가. **모르면(`undefined`) 거짓이다** — 없는 작업을 「끝났다」고 하지 않는다.
 *
 * 🔴 이 판정이 쓰이는 자리는 하나다 — **「지금 도는 모듈」 따라가기를 멈추는 것**
 *    (`followRunningDevice.ts`). 종착한 회차인데 마지막 모듈이 `WAITING_FOR_UNLOAD` 로 서
 *    있으면, 따라가기가 그 모듈을 계속 가리켜 환자 카드가 **「염색 진행 중 ◐」과 「완료 지남」을
 *    한 장에서 같이** 말했다(2026-09-22 실물).
 */
export function isTerminalBatch(batch: Pick<Batch, 'status'> | undefined): boolean {
  return batch !== undefined && TERMINAL_BATCH_STATUSES.includes(batch.status)
}

/**
 * 구독이 준 진행 → **3행 한 줄**. 재료가 하나도 없으면 `undefined` 다(그러면 요소를 안 그린다).
 *
 * 🔴 **산술 연산이 없다.** `step`/`total` 로 백분율을 만들지 않는다 — eric 지시가
 *    ctcells-core 에 박혀 있다(`app/resources/admin/pages/dashboard.js` — 「프론트에서
 *    계산하는 게 아니고 서버에서 계산해서 보내줘야 한다」 2026-09-15). 아래 시험이 그것을 지킨다.
 * 🔴 **라벨이 범위를 말한다** — 「모듈 내 스텝」이다. 이 수는 배치 전체가 아니라 모듈이 보고한
 *    것이고(`batch_progress` 본문 = `ExecutionStream` 동형), 「진행」이라 쓰면 배치 전체로 읽힌다.
 * ⚠ `0` 은 「없다」가 아니라 **첫 스텝**이다(`started` 가 `step=0`·`total=N` 으로 온다).
 *
 * 🔴 **`module_status` 의 시퀀스 이름을 «같이» 쓰지 않는다 — 실물에서 서로 어긋났다**
 *    (실서버 재생 2026-09-20). 그 값은 1초 주기 미러라 스텝별 진행 프레임보다 늦게 오고,
 *    나란히 그리면 한 줄이 **두 말을 한다**: 「place · 모듈 내 스텝 70/96 · move_to 시작」.
 *    ⇒ **같은 프레임이 준 `message` 를 먼저 쓰고**, 그것이 없을 때만 미러 이름으로 떨어진다.
 *    (진행 프레임이 아직 하나도 안 왔을 때 「wait_for_manual_load」만이라도 말하는 자리다.)
 */
export function stripStepText(progress: BatchProgressView | undefined): string | undefined {
  if (progress === undefined) return undefined
  const { sequence, message, eventType, action, moduleType } = progress
  /*
   * 🔴 **시퀀스 원값을 영문 그대로 흘리지 않는다**(eric 2026-10-11 「move_z 완료」 · 「move_aux_axis 완료」 — 장비 그림 아래와 같은 병).
   *    사전은 그 진행을 말한 모듈의 종류(`moduleType` · `module_status` 가 나름)로 고른다 — 모르면 사전 없이 원문 그대로(짐작하지 않는다).
   *    말은 머리(`action` 과 정확히 같을 때)만 바꾸고(`sequenceText` · 그림 아래 한 줄과 같은 함수), 말이 없을 때의 대타 `sequence` 는 이름째 바꾼다.
   */
  const dictionary = sequenceDictionaryOfModuleType(moduleType)

  /*
   * 🔴 **스텝 수는 여기 없다**(eric 2026-09-22 「모듈 내 스텝 이 텍스트는 빼고, 현재/전체 스텝 숫자는
   *    프로그레스바 맨 오른쪽에」). 수는 `stripStepCount()` 가 따로 내고 2행 오른끝에 선다.
   */
  const parts = [
    eventType !== undefined && (NOTABLE_EVENTS.has(eventType) || !speaksSequence(eventType))
      ? executionEventLabel(eventType)
      : undefined,
    // 🔴 같은 프레임이 준 말이 먼저다. 미러(`sequence`)는 그 말이 없을 때의 대타다
    // 🔴 생애 사건(취소 · 오류 · 시작 …)의 말은 모듈 내부 사유 원문이라 옮기지 않는다 — 사건 라벨이 앞에 이미 섰다(eric 2026-10-12 「취소됐다」)
    message !== undefined && speaksSequence(eventType)
      ? sequenceText(message, action, dictionary)
      : sequence === undefined
        ? undefined
        : sequenceText(sequence, sequence, dictionary),
  ].filter((part) => part !== undefined)

  return parts.length === 0 ? undefined : parts.join(' · ')
}

/**
 * 2행 오른끝의 **배치 전체 스텝 수** 「123/313」. 산술은 더하기뿐이다(`stripTotalSteps`).
 * ⚠ `0` 은 첫 스텝이다(「없다」가 아니다). `total` 이 없으면 `step` 만 적는다.
 * 🔴 `+?` 는 **정말 모를 때만**이다 — 지난 작업 기준 예상으로 채웠으면 숫자는 깔끔하게 두고 툴팁이 말한다
 *    (eric 2026-09-28).
 */
export function stripStepCount(
  progress: BatchProgressView | undefined,
  memory?: StepMemory,
): string | undefined {
  const sum = stripTotalSteps(progress, memory)
  if (sum === undefined) return undefined
  if (sum.total === undefined) return String(sum.step)
  return `${String(sum.step)}/${String(sum.total)}${sum.partial ? PARTIAL_MARK : ''}`
}

/**
 * 🔴 **분모가 아직 늘 수 있다는 표시.** 아직 시작 안 한 모듈의 총수는 서버 어디에도 없어서
 *    (모듈이 레시피를 컴파일한 결과이고 `started` 프레임으로 처음 온다 — 2026-09-23 실측)
 *    **실제로도 기억으로도** 모르는 모듈이 남으면 지금 아는 합이 전체가 아니다. 감추면 화면이
 *    **모르는 것을 아는 척**한다.
 * ⚠ 레시피의 `step_counts` 로 채우지 마라 — **다른 단위**다(머리말). 예상은 같은 레시피에서
 *   **모듈이 실제로 보고한 수**를 기억한 것만 쓴다(`stripStepMemory.ts`).
 */
const PARTIAL_MARK = '+?'

/** 배치 전체의 스텝 합. `total` 이 없으면 총수를 한 건도 모른다 */
export interface StripTotalSteps {
  /** 🔴 끝난 모듈의 총수 합 + 지금 모듈의 현재 스텝(eric 2026-09-23) */
  step: number
  /** 아는 모듈들의 총수 합(실제 + 기억). 하나도 모르면 없다 */
  total?: number
  /** 실제로도 기억으로도 모르는 모듈이 남아 있다 — 분모가 늘 수 있다 */
  partial: boolean
  /** 분모에 지난 작업 기준 **예상**이 섞였다 */
  estimated: boolean
}

/** 모듈 하나의 스텝 수와 그 출처 */
export interface MergedStep {
  step?: number | undefined
  total?: number | undefined
  /**
   * `heard` = 이 화면이 구독에서 들은 것 · `remembered` = **같은 배치**에서 전에 들은 것(새로 고침 복원) ·
   * `estimated` = 같은 레시피·버전·시료 수의 **지난 작업**에서 들은 총수(예상)
   */
  basis: 'heard' | 'remembered' | 'estimated'
}

/**
 * 모듈별 스텝 수를 **실제 → 같은 배치의 기억 → 지난 작업 기준 예상** 순으로 채운다(eric 2026-09-28).
 *
 * 🔴 **실제가 늘 이긴다.** 들은 모듈은 기억을 보지 않는다(총수만 비었으면 같은 배치의 기억으로 메운다 —
 *    같은 배치 · 같은 모듈의 수라 다른 값이 아니다).
 * 🔴 예상은 **총수뿐이다.** 시작 안 한 모듈의 현재 스텝은 지어내지 않는다(합에서는 0 으로 더해진다 — 아직
 *    한 스텝도 안 했으니 합이 거짓이 되지 않는다).
 */
export function mergeModuleSteps(
  progress: BatchProgressView | undefined,
  memory: StepMemory = EMPTY_STEP_MEMORY,
): ReadonlyMap<ModuleKey, MergedStep> {
  const heard = stripModuleSteps(progress)
  const out = new Map<ModuleKey, MergedStep>()
  for (const key of MODULE_KEYS) {
    const live = heard.get(key)
    const remembered = memory.batch.get(key)
    if (live !== undefined) {
      const total = live.total ?? remembered?.total
      out.set(key, {
        ...(live.step === undefined ? {} : { step: live.step }),
        ...(total === undefined ? {} : { total }),
        basis: 'heard',
      })
      continue
    }
    if (remembered !== undefined) {
      out.set(key, {
        ...(remembered.step === undefined ? {} : { step: remembered.step }),
        ...(remembered.total === undefined ? {} : { total: remembered.total }),
        basis: 'remembered',
      })
      continue
    }
    const estimate = memory.estimate.get(key)
    if (estimate !== undefined) out.set(key, { total: estimate, basis: 'estimated' })
  }
  return out
}

/**
 * **배치 전체의 현재/전체 스텝** — eric 2026-09-23 「맨 오른쪽은 각각의 모듈 스텝을 더한 전체,
 * 현재 스텝은 이전에 완료된 모듈 스텝 + 현재 진행 중인 스텝을 더한 것으로」.
 *
 * 🔴 **산술은 «더하기»뿐이다.** 끝난 모듈은 서버가 `step === total` 로 말해 주므로
 *    (실물 전수 확인 — `complete` 프레임이 `step=total`), **아는 모듈의 `step` 을 전부 더하면**
 *    그것이 곧 「끝난 모듈 합 + 지금 스텝」이다. 「끝났는지」를 우리가 판정하지 않는다.
 * 🔴 **모르는 모듈은 0 으로 세지 않는다** — 분모에서도 빠지고 `partial` 이 선다.
 *    기억으로 채운 모듈은 분모에 들어가고 예상이면 `estimated` 가 선다(eric 2026-09-28).
 * ⚠ 단위는 **모듈이 컴파일한 스텝**이다. 레시피 `step_counts`(원심 57 · 어셈 101 · 염색 71)와
 *   **다른 수**이고(같은 배치 실측: 96 · 164 · 359) 섞어 더하면 거짓이 된다.
 * ⚠ 모듈별 수를 한 건도 모르면(구독이 아직 · 목 데이터) **지금 프레임 하나**를 그 자리로
 *   쓴다 — 그때도 아는 모듈이 하나뿐이라 `partial` 이다.
 */
export function stripTotalSteps(
  progress: BatchProgressView | undefined,
  memory?: StepMemory,
): StripTotalSteps | undefined {
  const counts = mergeModuleSteps(progress, memory)
  const known = [...counts.values()]
  if (known.length === 0) {
    const step = progress?.step
    if (step === undefined) return undefined
    const total = progress?.total
    return {
      step,
      ...(total === undefined ? {} : { total }),
      partial: true,
      estimated: false,
    }
  }
  let step = 0
  let total = 0
  let totalKnown = 0
  let estimated = false
  for (const entry of known) {
    step += entry.step ?? 0
    if (entry.total !== undefined) {
      total += entry.total
      totalKnown += 1
      if (entry.basis === 'estimated') estimated = true
    }
  }
  return {
    step,
    ...(totalKnown === 0 ? {} : { total }),
    /* 🔴 세 모듈을 다 «알기» 전에는 분모가 전체가 아니다 */
    partial: totalKnown < MODULE_KEYS.length,
    estimated,
  }
}

/**
 * 모듈 종류별 스텝 수 — **구간 아래 숫자와 구간 폭이 같이 읽는 한 자리.**
 *
 * 🔴 **`MODULE_KEYS` 로 옮겨 담는다.** 구독은 `module_id`(`staining-1`)로 말하고 화면은
 *    종류(`stain`)로 그린다 — 그 사상은 `module_status` 가 준 `module_type` 하나뿐이다.
 *    종류를 모르는 모듈(6축 로봇 `collaborative_6axis` 등)은 **빠진다** — 작업 줄 구간이
 *    셋이라 넷째를 그릴 자리가 없고, 지어낸 자리를 만들지 않는다.
 */
export function stripModuleSteps(
  progress: BatchProgressView | undefined,
): ReadonlyMap<ModuleKey, { step?: number | undefined; total?: number | undefined }> {
  const out = new Map<ModuleKey, { step?: number | undefined; total?: number | undefined }>()
  for (const entry of progress?.moduleSteps ?? []) {
    const key = moduleTypeKey(entry.moduleType)
    if (key === undefined || !(MODULE_KEYS as readonly string[]).includes(key)) continue
    out.set(key as ModuleKey, {
      ...(entry.step === undefined ? {} : { step: entry.step }),
      ...(entry.total === undefined ? {} : { total: entry.total }),
    })
  }
  return out
}

/**
 * 생애 라벨을 **붙이는** 축. 🔴 나머지(`started`·`progress`·`step_complete`·`telemetry`)는
 * **일상 축**이라 안 붙인다 — 수와 모듈이 한 말이 이미 그것을 설명하고, 라벨을 더하면
 * 「모듈 내 스텝 1/96 · 스텝 완료 · wait_for_manual_load 완료」처럼 같은 말이 두 번 나온다
 * (실서버 재생 2026-09-20에서 실제로 그렇게 나왔다).
 */
const NOTABLE_EVENTS: ReadonlySet<BatchProgressView['eventType']> = new Set([
  'complete',
  'error',
  'cancelled',
  'aborted',
  'paused',
  'resumed',
])

/**
 * `Batch`(계약 13필드) + 구독 진행 → 작업 줄 표시값.
 *
 * 🔴 **진행률 · 경과 · 총 시간 · 편차를 «채우지 않는다».** REST 에 없고
 *    (`app/views/batches.py` `_dto()`) **구독에도 없다** — `ExecutionStream` 은 `step`·`total`
 *    뿐이다(계약·실물 전수 2026-09-20 · `docs/backend-requests.csv` BR-09).
 *    `—` 로 채워 두면 「서버가 준 값」으로 오해받는다.
 * 🔴 채워지는 것은 **3행 한 줄**과 **2행 구간 진행바**(`stripSegments` — 구독의 `step/total` 을
 *    그 모듈 구간 안의 채움으로 옮긴 것)이고, 둘 다 구독이 말했을 때만이다.
 */
export function toStripBatch(
  batch: Batch,
  recipeNames: ReadonlyMap<string, string>,
  progress?: BatchProgressView,
  now?: number,
  steps?: StripStepExtras,
): BatchStripBatch {
  const currentStep = stripStepText(progress)
  const moduleName = stripModuleName(progress)
  const segments = stripSegments(progress, steps?.memory, steps?.runningModuleType)
  const elapsed = stripElapsedText(batch, now)
  const stepCountText = stripStepCount(progress, steps?.memory)
  const totals = stripTotalSteps(progress, steps?.memory)
  /*
   * 🔴 「지금 칸의 실행이 끝났다」는 **진행 프레임이 말한 공정 장비(원심 · 어셈 · 염색 — `MODULE_KEYS`)**에 대해서만 선다 — 6축(`robot`)의
   *    종착은 공정 칸이 아니다(작업 줄 칸 셋에 없다). 미러(`runningModuleType`)로 세운 지금 칸에는
   *    종착 신호가 없다. 서버 원값 `complete` 하나만 본다(`error` · `cancelled` 는 끝이 아니라 멈춤이다 — 배치 상태가 말한다).
   */
  const settledKey = moduleTypeKey(progress?.moduleType)
  const settled =
    steps?.flow !== undefined &&
    progress?.eventType === 'complete' &&
    settledKey !== undefined &&
    isModuleKey(settledKey)
  const loadWait = steps?.flow?.loadWait
  const transferring = steps?.flow?.transferring === true
  return {
    id: batch.batch_id,
    name: batchDisplayName(batch),
    ...(batch.started_at == null ? {} : { started: true as const }),
    // 1행 「시작 <연월일 시분초>」 — 재료는 같은 `started_at` 이다. 없으면 키가 없고 화면이 「시작 전」을 적는다
    ...(batch.started_at == null ? {} : { startedAtText: fullDateTimeText(batch.started_at) }),
    // 계약에 배치→레시피 «이름»이 없다. `/recipes` 를 따로 받아 붙이고, 없으면 id 가 남는다
    recipeName: recipeNames.get(batch.recipe_id) ?? batch.recipe_id,
    status: batch.status,
    /*
      🔴 **서버가 준 까닭을 «버리지 않는다».** 이 칸이 주 버튼을 `resume` ↔ `recover` 로 가른다
         (`BatchStripBatch.reviewReason` 주석 · BR-50). 2026-09-21 까지 여기서 떨어뜨렸고,
         그래서 격리가 걸린 배치에 **락을 안 놓는 버튼**이 주 버튼으로 떠 있었다.
      ⚠ `null` 은 「판정 대상이 아니다」이지 「모른다」가 아니다 — 그때는 키 자체를 두지 않는다.
    */
    ...(batch.review_reason == null ? {} : { reviewReason: batch.review_reason }),
    // 🔴 잇는 단추 · 확인할 것 · 시간 원자 방식 — 서버 칸 그대로(`resumeRoute.tsx` 머리말 · 백엔드 0.28.15)
    ...(batch.resume_route == null ? {} : { resumeRoute: batch.resume_route }),
    ...(batch.resume_checks == null || batch.resume_checks.length === 0
      ? {}
      : { resumeChecks: batch.resume_checks }),
    ...((batch.resume_timed_atom_choices?.length ?? 0) >= 2
      ? { timedAtomChoices: batch.resume_timed_atom_choices }
      : {}),
    ...(moduleName === undefined ? {} : { moduleName }),
    ...(currentStep === undefined ? {} : { currentStep }),
    ...(segments === undefined ? {} : { segments }),
    ...(elapsed === undefined ? {} : { elapsedText: elapsed.text, elapsedKind: elapsed.kind }),
    ...(elapsed?.sinceMs === undefined ? {} : { elapsedSinceMs: elapsed.sinceMs }),
    ...(stepCountText === undefined ? {} : { stepCountText }),
    // 요약 카드의 재료 — 같은 합이다. 총수를 한 건도 모르면 `total` 키가 없다
    ...(totals === undefined
      ? {}
      : {
          totalSteps: {
            step: totals.step,
            ...(totals.total === undefined ? {} : { total: totals.total }),
          },
        }),
    ...(totals?.partial === true ? { stepCountPartial: true } : {}),
    ...(totals?.estimated === true ? { stepCountEstimated: true } : {}),
    ...(settled ? { stageSettled: true as const } : {}),
    ...(loadWait === undefined ? {} : { loadWait }),
    ...(transferring ? { transferring: true as const } : {}),
    ...(steps?.flow?.transferRoutes === undefined
      ? {}
      : { transferRoutes: steps.flow.transferRoutes }),
    ...(steps?.flow?.trayHolds === undefined || steps.flow.trayHolds.length === 0
      ? {}
      : { trayHolds: steps.flow.trayHolds }),
    ...(steps?.flow?.haltAt === undefined ? {} : { haltAt: steps.flow.haltAt }),
  }
}

/**
 * **멈춘 모듈** — 일시정지된 작업(`status: paused`)의 `halt_module_id` 를 모듈 목록(`module_type`)으로 공정 키에 잇는다.
 * 🔴 서버 값만 — `halt_module_id` 가 없거나 · 목록에서 종류를 못 찾거나 · 공정 장비(원심 · 어셈 · 염색)가 아니면 `undefined`(다음 · 지금 칸으로 짐작하지 않는다).
 * 🔴 **전체 비상정지 · 안전정지(`halt_group: safety` · 까닭 `safety_stop`)면 `undefined`** — 묶음 배지를 달지 않는다(eric 2026-10-29 「어셈블러에서
 *    E-STOP 을 눌렀는데 염색기에 안전정지가 있어?」). 실물(cc77864f · 어셈블러가 370/415 로 돌던 중 E-STOP)에서 `halt_module_id` 가 `staining-1`
 *    이었다 — 계약은 «멈춤 사유 행의 module_id» 인데 전체 비상정지는 특정 모듈이 까닭이 아니라 아무 모듈이나 가리킨다. 그때는 머리 줄 「안전정지」
 *    칩이 말한다. 모듈이 까닭인 멈춤(사람 · 자동 일시정지에 `halt_module_id` 가 있을 때)만 그 묶음에 호박 「일시정지」 배지다.
 * ⚠ `error`(실행 실패 등)는 대상 밖이다 — 종전 칸 표시 그대로다.
 */
export function stripHaltAt(
  batch: Pick<Batch, 'status' | 'halt_module_id' | 'halt_group'> | undefined,
  modules: readonly { module_id: string; module_type: string }[] | undefined,
): BatchStripBatch['haltAt'] {
  if (
    batch?.status !== 'paused' ||
    batch.halt_group === 'safety' ||
    batch.halt_module_id == null ||
    modules === undefined
  )
    return undefined
  const type = modules.find((module) => module.module_id === batch.halt_module_id)?.module_type
  const key = type === undefined ? undefined : moduleTypeKey(type)
  if (key === undefined || !isModuleKey(key)) return undefined
  return { key, kind: 'pause' }
}

/** 모듈 스냅샷에서 이 함수가 읽는 칸만(`GET /modules` 항목 · 구독 미러와 같은 모양) */
export interface StripLoadWaitModule {
  module_type?: string | null
  state?: string | null
  current_execution?: unknown
}

/**
 * **이 배치의 실행을 쥔 모듈이 사람 적재 대기인가** — 대기 영역 「원심분리기 대기」의 재료(`BatchStripBatch.loadWait`).
 * 🔴 서버 두 칸을 맞댈 뿐이다 — `current_execution.batch_id` 가 이 배치이고 `state` 가 적재 대기 어휘(`MANUAL_LOAD_WAIT_STATES`)다.
 *    2026-10-06 dev2 실측(batch `7d73514f`): 시작 1초 뒤 원심분리기가 실행을 쥐고 `IDLE → WAITING_FOR_MANUAL_TRAY_LOAD`, 사람이 넣자
 *    `LOADING`. 그 사이가 원심분리기 대기다. 종류는 서버 `module_type` 으로 고른다(ID 에서 읽어내지 않는다). 못 맞추면 `undefined`.
 */
export function stripLoadWait(
  modules: readonly StripLoadWaitModule[] | undefined,
  batchId: string | undefined,
): { key: string; state: string } | undefined {
  if (modules === undefined || batchId === undefined) return undefined
  for (const snapshot of modules) {
    const state = snapshot.state
    if (typeof state !== 'string' || !MANUAL_LOAD_WAIT_STATES.has(state)) continue
    const execution = snapshot.current_execution
    const owner =
      typeof execution === 'object' && execution !== null
        ? (execution as { batch_id?: unknown }).batch_id
        : undefined
    if (owner !== batchId) continue
    // 공정 장비(원심 · 어셈 · 염색)만 — 6축(`robot`)은 대기 칸이 없다
    const key = moduleTypeKey(snapshot.module_type)
    if (key !== undefined && isModuleKey(key)) return { key, state }
  }
  return undefined
}

/**
 * 1행 「담당」의 이름 — **지금 세션의 사람이 만든 작업일 때만** 그 표시 이름(이름 → 로그인 ID)이다.
 *
 * 🔴 `Batch.created_by` 는 계정 ID 다. 이름은 admin 전용 `GET /users` 로만 풀리고(`batchOwnerColumn.tsx`)
 *    작업 줄은 그 호출을 하지 않는다 — 그래서 풀 수 있는 것은 «나» 하나다. 남이 만든 작업은 `undefined`
 *    (칸 자체가 안 선다). **ID 를 그대로 적지 않는다**(2026-09-30 「admin 이 아닌 표면에 id 열로 되살리지 마라」).
 */
export function stripOwnerText(
  batch: Pick<Batch, 'created_by'> | undefined,
  me: { user_id?: string; name?: string | null; username?: string | null } | null | undefined,
): string | undefined {
  if (batch?.created_by == null || me?.user_id === undefined || batch.created_by !== me.user_id)
    return undefined
  return [me.name, me.username].find((v) => typeof v === 'string' && v.trim() !== '') ?? undefined
}

/**
 * 진행 프레임 밖에서 오는 두 재료(eric 2026-09-28).
 * - `memory` — 이 브라우저가 기억한 수(`stripStepMemory.ts`)
 * - `runningModuleType` — 미러가 「그 배치의 실행을 쥐었다」고 한 모듈의 종류(`useBatchRunningModuleType`).
 *   진행 프레임이 말한 모듈이 없을 때만 구간의 「지금」 자리를 세운다
 * 🔴 구독이 stale 이면 컨테이너가 **둘 다 안 넘긴다** — 진행 표시를 접는 규약 그대로다.
 */
export interface StripStepExtras {
  memory?: StepMemory | undefined
  runningModuleType?: string | undefined
  /**
   * 준비 · 완료 영역의 재료(`BatchStripProps.flowZones` 셸만 넘긴다). 넘기면 `stageSettled` 를 진행 프레임에서 세우고,
   * `loadWait` · `transferring` 을 그대로 싣는다. 안 넘기면 셋 다 없다 — 종전 칸 셋 그대로다.
   */
  flow?:
    | {
        loadWait?: { key: string; state: string } | undefined
        transferring?: boolean | undefined
        transferRoutes?: BatchStripBatch['transferRoutes']
        trayHolds?: BatchStripBatch['trayHolds']
        haltAt?: BatchStripBatch['haltAt']
      }
    | undefined
}

/**
 * 1행 시간 칸 — eric 2026-09-22 「시간 정보는 없어?」.
 *
 * 🔴 **재료는 응답의 두 시각뿐이다.** `started_at`·`completed_at` 은 `Batch` 13필드에 있다(실물 확인).
 *    - 종착했으면 **소요** = 두 시각의 뺄셈(`durationText` · 이력 「기간」 열과 같은 식)
 *    - 돌고 있으면 **경과** = 지금 − `started_at`. 멈춤(`paused`)도 `completed_at` 이 없으니 경과가 계속 흐른다
 *      (종전 규칙 그대로). 「지금」은 화면 시계를 읽을 뿐이다(DP-01 의 폴링이 아니다 — 서버에 묻는 것이 없다)
 *    - 시작 전(`started_at` 없음)이면 칸 자체가 없다
 * 🔴 표기는 **시:분:초**(`HH:MM:SS`)다 — eric 2026-09-28 「지금 시분 현재 시간 보여주는 거 있는데.. 그거 초도
 *    넣어주고」. 소요·총 시간도 같은 꼴이라야 「걸린 / 전체」로 나란히 선다.
 * 🔴 돌고 있으면 `sinceMs`(= `started_at`)를 같이 낸다. **초를 세는 것은 시계 조각(`StripClock`)이다** —
 *    1초마다 작업 줄 전체를 다시 그리지 않게 하려고 이 함수는 한 번 뺀 값만 낸다.
 * 🔴 **총 예상·잔여(ETA)는 없다** — 계약에도 실물에도 없어(BR-09) 적지 않는다. 진행률 % 도 같다.
 * ⚠ `now` 를 안 넘기면(시험·정적 그리기) 경과를 만들지 않는다 — 시계를 여기서 읽지 않는다.
 */
export function stripElapsedText(
  batch: Pick<Batch, 'started_at' | 'completed_at'>,
  now: number | undefined,
): { text: string; kind: 'elapsed' | 'duration'; sinceMs?: number } | undefined {
  if (batch.started_at == null) return undefined
  const startMs = Date.parse(batch.started_at)
  if (Number.isNaN(startMs)) return undefined
  if (batch.completed_at != null) {
    const endMs = Date.parse(batch.completed_at)
    if (Number.isNaN(endMs) || endMs < startMs) return undefined
    return { text: clockText(endMs - startMs), kind: 'duration' }
  }
  if (now === undefined || now < startMs) return undefined
  return { text: clockText(now - startMs), kind: 'elapsed', sinceMs: startMs }
}

/**
 * 걸린 밀리초 → `HH:MM:SS`. 시는 두 자리 이상이면 그대로 늘어난다(`100:00:00`).
 * 음수는 `00:00:00` 이다 — 기기 시계가 서버보다 늦어 잠깐 음수가 나와도 거꾸로 세지 않는다.
 */
export function clockText(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000))
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(Math.floor(sec / 3600))}:${pad(Math.floor(sec / 60) % 60)}:${pad(sec % 60)}`
}

/**
 * 2행 구간 진행바 — eric 2026-09-22 「프로그레스바 쪽에 진행률 지금 아무것도 안 보이는데 보이게 해줘」.
 *
 * ```
 * [원심분리기 ████████] [어셈블러 ███░░░░░] [염색기 ░░░░░░░░]
 *   지난 구간 = 가득        현재 = step/total        다음 = 빈 채
 * ```
 *
 * 🔴 **재료는 구독이 준 수와 그 수의 기억뿐이다** — 지금 모듈(`module_type`)과 모듈별 `step/total`,
 *    그리고 이 브라우저가 전에 들은 수(`stripStepMemory.ts` · eric 2026-09-28). 구간 폭은 모듈별 총수의
 *    비율이다(`segmentWeights`). 스크린리더의 전체 % 는 「구간 채움의 가중 평균」이지 시간 진행률이 아니다.
 * 🔴 **구간 순서는 제품 정의의 공정 순서**(원심 → 어셈 → 염색 · `MODULE_KEYS` 의 순서)다. 지금
 *    모듈보다 앞 구간은 지났다고 보고 가득 채운다 — 배치가 뒤로 돌아가는 경로는 계약에 없다.
 * 🔴 **모르면 안 그린다.** 지금 모듈도 모르고 수도 하나 없으면 `undefined` 라 빈 트랙이 종전대로 남는다.
 * ⚠ `total` 이 0 이면 나눗셈을 안 하고 그 구간을 빈 채로 둔다 — 값을 지어내지 않는다.
 */
export function stripSegments(
  progress: BatchProgressView | undefined,
  memory?: StepMemory,
  runningModuleType?: string,
): readonly BatchStripSegment[] | undefined {
  /*
   * 🔴 「지금」 자리는 **진행 프레임이 말한 모듈이 먼저**다. 그것이 없을 때만(새로 고친 직후 · 보류로 서서
   *    진행 프레임이 0건) 미러가 「그 배치의 실행을 쥐었다」고 한 모듈로 선다(eric 2026-09-28).
   */
  const fromFrame = moduleTypeKey(progress?.moduleType)
  const current = fromFrame ?? moduleTypeKey(runningModuleType)
  const at = current === undefined ? -1 : MODULE_KEYS.indexOf(current as ModuleKey)
  const counts = mergeModuleSteps(progress, memory)
  /*
   * 🔴 **지금 자리를 모르면 «끝남/지금/아직»을 말하지 않는다.** 그래도 기억한 수가 있으면 구간 폭과
   *    각 구간의 채움(그 모듈이 말한 step/total)은 그린다 — 새로 고친 직후 회색 한 줄 대신이다.
   *    어느 것이 지금인지는 추론하지 않는다.
   */
  if (at < 0 && counts.size === 0) return undefined
  const frameCount =
    fromFrame !== undefined && progress !== undefined
      ? { step: progress.step, total: progress.total }
      : undefined
  const weights = segmentWeights(counts)
  return MODULE_KEYS.map((key, i) => {
    const merged = counts.get(key)
    const count = i === at && frameCount !== undefined ? frameCount : merged
    const countText = stepPairText(count)
    const label = moduleLabel(key)
    const estimated = merged?.basis === 'estimated' && !(i === at && frameCount !== undefined)
    const fill = fillOf(count)
    const title = [
      countText === undefined ? label : `${label} ${countText}`,
      estimated ? t('strip.moduleStepEstimated') : undefined,
    ]
      .filter((part) => part !== undefined)
      .join(' · ')
    return {
      key,
      label,
      /* 좁은 칸에서 이름 대신 서는 첫 글자(원 · 어 · 염). 어느 것이 설지는 칸 폭이 정한다(CSS) */
      shortLabel: label.slice(0, 1),
      weight: weights[i] ?? 1,
      progress: at < 0 ? fill : i < at ? 1 : i === at ? fill : 0,
      title,
      /* 🔴 상태는 위 채움과 같은 규칙 하나에서 나온다 — 앞은 끝남, 지금, 뒤는 아직 안 옴. 지금을 모르면 없다 */
      ...(at < 0 ? {} : { state: i < at ? 'done' : i === at ? 'current' : 'pending' }),
      ...(i === at ? { current: true } : {}),
      ...(countText === undefined ? {} : { countText }),
      /* 공정 칸이 굵기를 따로 주려고 수 자체도 받는다 — `countText` 와 같은 재료다 */
      ...(count?.step === undefined ? {} : { step: count.step }),
      ...(count?.total === undefined ? {} : { total: count.total }),
      ...(estimated ? { estimated: true } : {}),
    } satisfies BatchStripSegment
  })
}

/** 구간 안 채움 0~1. ⚠ `total` 이 0 이거나 모르면 0 — 나눗셈으로 값을 지어내지 않는다 */
function fillOf(
  count: { step?: number | undefined; total?: number | undefined } | undefined,
): number {
  const step = count?.step
  const total = count?.total
  return step !== undefined && total !== undefined && total > 0 ? Math.min(1, step / total) : 0
}

/**
 * 구간 하나의 **`현재/총수`** — eric 2026-09-23 「각각의 모듈 끝나는 부분 아래에 `23/93`」.
 * 🔴 **총수를 모르면 아무것도 안 적는다.** `0/?` 도 `—` 도 아니다 — 빈 칸이 「아직 안 들었다」다.
 * 🔴 **`0` 을 지어내지 않는다.** 아직 시작 안 한 모듈은 `step` 도 총수도 우리에게 없다.
 */
function stepPairText(
  count: { step?: number | undefined; total?: number | undefined } | undefined,
): string | undefined {
  if (count?.total === undefined) return undefined
  return `${String(count.step ?? 0)}/${String(count.total)}`
}

/**
 * 🔴 **구간이 아주 짧아도 이 몫만큼은 선다**(전체의 12%). 이름 칩과 `현재/총수` 숫자가
 *    겹쳐 읽히지 않을 최소치다. 해상도 숫자가 아니라 **비율**이라 화면 크기와 무관하다.
 * ⚠ 물리적 최소 폭(px)은 CSS 토큰(`--prog-seg-min`)이 따로 지킨다 — 둘은 다른 축이다.
 */
const SEGMENT_MIN_SHARE = 0.12

/**
 * 구간 폭 — eric 2026-09-23 「스텝 수 비례해서 해줘」.
 *
 * 🔴 **셋의 총수를 다 «알 때»만 비례한다.** 하나라도 모르면 가중치를 지어내는 것이 되므로 **등분**이다.
 *    서버는 총수를 모듈이 시작할 때(`started` 프레임) 처음 주므로, 기억이 없으면 비례가 켜지는 시점은
 *    마지막 모듈이 시작한 뒤다. 같은 레시피를 한 번 돌린 브라우저는 **지난 작업 기준 예상**으로
 *    처음부터 비례한다(eric 2026-09-28 「각각의 모듈별 스텝의 양으로.. 프로그레스바 길이도」).
 * 🔴 짧은 구간도 `SEGMENT_MIN_SHARE` 만큼은 선다. 깎은 몫은 **긴 구간들이 비례로 나눠** 낸다.
 */
function segmentWeights(
  counts: ReadonlyMap<ModuleKey, { total?: number | undefined }>,
): readonly number[] {
  const totals = MODULE_KEYS.map((key) => counts.get(key)?.total)
  if (totals.some((value) => value === undefined || value <= 0)) return MODULE_KEYS.map(() => 1)
  const known = totals as readonly number[]
  const sum = known.reduce((acc, value) => acc + value, 0)
  const shares = known.map((value) => value / sum)
  const short = shares.filter((share) => share < SEGMENT_MIN_SHARE).length
  if (short === 0) return shares
  const longSum = shares
    .filter((share) => share >= SEGMENT_MIN_SHARE)
    .reduce((acc, share) => acc + share, 0)
  const room = 1 - SEGMENT_MIN_SHARE * short
  /* 긴 구간이 하나도 없으면(전부 짧다) 등분으로 떨어진다 — 나눌 몫이 없다 */
  if (longSum <= 0) return MODULE_KEYS.map(() => 1)
  return shares.map((share) =>
    share < SEGMENT_MIN_SHARE ? SEGMENT_MIN_SHARE : (share / longSum) * room,
  )
}

/**
 * 3행 맨 앞의 **모듈 이름**. 「모듈 내 스텝 112/164 · place 시작」만으로는 **어느 장비의
 * 스텝인지**를 알 수 없다 — 배치가 모듈 3대를 거치므로 같은 수가 장비마다 다른 뜻이다.
 *
 * 🔴 **재료는 서버가 준 두 칸뿐이다.** 봉투의 `module_id`(진행 프레임 전건에 실린다 —
 *    실물 2,300건 실측)와 `module_status` 가 나른 `module_type` 이다. ID 에서 종류를
 *    «읽어내지» 않는다.
 * 🔴 **모르면 안 붙인다.** 「알 수 없는 모듈」을 만들면 화면이 모르는 것을 아는 척한다.
 *    그때는 단계 문구만 그려진다 — 종전과 같은 모습이니 나빠지지 않는다.
 */
export function stripModuleName(progress: BatchProgressView | undefined): string | undefined {
  return moduleTypeLabel(progress?.moduleType)
}

/** `/recipes` 응답에서 id → 이름 표. 목록이 없으면 빈 표다(이름 자리에 id 가 남는다) */
export function stripRecipeNames(
  recipes: readonly Recipe[] | undefined,
): ReadonlyMap<string, string> {
  return new Map((recipes ?? []).map((recipe) => [recipe.recipe_id, recipe.name]))
}

/**
 * 리스 보관소 → 제어락 칩 표시값.
 *
 * 🔴 **서버가 준 `holder` 는 화면에 띄우지 않는다.** 가드가 내는 409 의 `context.holder` 는 세션 ID 이고
 *    (`app/core/control_guard.py` · `docs/backend-requests.csv` BR-03), 획득 응답의 `holder` 는
 *    `holder_display()` 가 `user_session` 을 조인해 내는 **`user_id`(UUID)** 다(2026-09-22 실물 —
 *    eric 「획득 부분에서 uuid 말고 해당 유저 계정으로 해줘」). 둘 다 사람이 읽는 값이 아니다.
 *    ⇒ **내 락은 내 계정 이름**(`me` — 표시 이름, 없으면 로그인 ID)으로 적고, 남의 락은 이름 없이
 *    「다른 사용자가 보유」라고만 말한다. 서버 `holder` 가 username 을 실어 주면 그때 남의 락에도 쓴다.
 * 🔴 **`noperm` 을 쓰지 않는다.** 그 얼굴은 획득 버튼을 통째로 «숨긴다» — 규약은
 *    「권한 없는 버튼은 숨기지 않고 **비활성 + 사유**」다(CLAUDE.md RBAC). 권한 없음은
 *    획득 버튼의 비활성 사유(`control 권한 없음`)로 나간다.
 * 🔴 **카운트다운은 표시일 뿐이다.** `expiresAtMs` 는 서버가 준 만료 시각을 그대로 옮긴 것이고
 *    (eric 확정 2026-09-21), 초읽기는 `ControlLockChip` 이 1초 타이머로 그린다. 상태(`kind`)는
 *    여기서 여전히 서버 값만 따른다 — 카운트다운이 0 이 됐다고 `mine` 이 `expired` 로 바뀌지 않는다.
 */
export function toControlLockView(lock: ControlLockLike, me?: string): ControlLockView {
  switch (lock.status) {
    case 'unknown':
      return { kind: 'unknown' }
    case 'mine':
      return {
        kind: 'mine',
        holder: me ?? t('lock.heldByMe'),
        ...(lock.lease === null ? {} : { expiresAtMs: lock.lease.expiresAtMs }),
        ...(lock.lease?.receivedAtMs === undefined
          ? {}
          : { leaseReceivedAtMs: lock.lease.receivedAtMs }),
      }
    case 'other': {
      /*
       * 「○○ 보유」(히나 조사 1-2 · `GET /control-token` · `control_lock` 사건의 `holder.display_name`).
       * 이름을 모르면 「다른 사용자가 보유 · 이름 모름」 — UUID 를 드러내지 않는다
       */
      const name = lock.displayName?.trim()
      return {
        kind: 'other',
        holder:
          name === undefined || name === '' ? t('lock.holderUnknown') : t('lock.heldBy', { name }),
      }
    }
    case 'expired':
      return { kind: 'expired' }
    default:
      return { kind: 'none' }
  }
}

export function stripTransferRoutes(
  tracking: BatchTracking | undefined,
  batchId: string | undefined,
  modules: readonly { module_id: string; module_type: string }[] | undefined,
): NonNullable<BatchStripBatch['transferRoutes']> {
  if (tracking === undefined || tracking.batch_id !== batchId || modules === undefined) return []
  const types = new Map(
    modules.map((module) => [module.module_id, moduleTypeKey(module.module_type)]),
  )
  return tracking.trays.flatMap((tray) => {
    if (tray.where !== 'robot' && tray.where !== 'in_transit') return []
    const source = types.get(tray.last_module_id ?? '')
    const destination = types.get(tray.transit?.dest_module_id ?? '')
    if (destination === undefined || !isModuleKey(destination)) return []
    return [{ ...(source !== undefined && isModuleKey(source) ? { source } : {}), destination }]
  })
}

/**
 * **트레이 격리 · 낙하 표지** — 그 배치 추적의 트레이가 `quarantined` · `dropped` 이면 **떠나기 전 자리의 모듈**
 * (`TrayTrack.last_module_id` · 서버 값 그대로) 칸에 표지를 단다(07 §4.12 · 계약 설명 「운전 화면은 격리·낙하를 이 모듈 종의 단계 칸에 표지로 둔다」).
 * 🔴 화면이 위치를 계산하지 않는다 — `last_module_id` 가 없거나 모듈 목록에서 종류를 못 찾으면 표지를 안 단다(다음 · 앞 장비로 짐작하지 않는다).
 * 🔴 공정 장비(원심 · 어셈 · 염색)만 — 6축은 작업 줄 칸이 없다.
 */
export function stripTrayHolds(
  tracking: BatchTracking | undefined,
  batchId: string | undefined,
  modules: readonly { module_id: string; module_type: string }[] | undefined,
): NonNullable<BatchStripBatch['trayHolds']> {
  if (tracking === undefined || tracking.batch_id !== batchId || modules === undefined) return []
  const types = new Map(
    modules.map((module) => [module.module_id, moduleTypeKey(module.module_type)]),
  )
  return tracking.trays.flatMap((tray) => {
    if (tray.where !== 'quarantined' && tray.where !== 'dropped') return []
    const key = types.get(tray.last_module_id ?? '')
    if (key === undefined || !isModuleKey(key)) return []
    return [
      {
        key,
        where: tray.where,
        tray: tray.name,
        ...(tray.whereabouts == null ? {} : { whereabouts: tray.whereabouts }),
      },
    ]
  })
}

/**
 * **시료 처분 대기 안내**(빈 띠 한 줄 · DP-02) — `GET /batches?status=aborted&review=pending&limit=1` 의 맨 앞 한 건.
 *
 * 🔴 [중지]하면 서버가 그 작업을 `aborted` + `review_reason=sample_disposition` 으로 둔다(시료가 아직 진행 중 · 판정 대기 — core
 *    `batches._disposition_clause`). 작업 띠는 종착한 작업을 바로 비우므로(`stripPick.ts`) 처분할 것을 사람이 놓친다 — 그래서
 *    빈 띠가 «가장 최근 처분 대기 작업» 한 줄을 세운다. 처분 창구는 작업 상세(이력)다.
 * 🔴 **서버 값 그대로다** — `review_reason` 이 `sample_disposition` 인 줄만 · 서버 차례의 맨 앞(가장 최근 종착). 서버가 처분 대기를 풀면
 *    목록에서 빠져 안내도 사라진다(구독 `batch_status` 가 `keys.batches` 를 무효화 · 새 주기 조회 없음).
 * 🔴 `more` = 서버 총수 − 1(총수를 모르면 0 — 지어내지 않는다).
 * ⚠ `dismissed` = 사람이 × 로 «이 화면에서» 숨긴 작업. 서버 상태는 그대로이고, 그 작업만 숨긴다 — 더 새로운 처분 대기가 생기면 다시 선다.
 */
export function stripDisposition(
  list: Pick<Listed<Batch>, 'items' | 'total_count'> | undefined,
  dismissed: string | undefined,
): { batchId: string; shortId: string; more: number } | undefined {
  const first = list?.items[0]
  if (first === undefined) return undefined
  if (first.status !== 'aborted' || first.review_reason !== 'sample_disposition') return undefined
  if (first.batch_id === dismissed) return undefined
  const total = list?.total_count
  return {
    batchId: first.batch_id,
    shortId: first.batch_id.slice(0, 8),
    more: total == null ? 0 : Math.max(0, total - 1),
  }
}
